import { ColumnTable, Icon, TextOutlinedInput } from '@octano/global-ui';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { prefix } from '..';
import { TableCurriculumDegreeActivity } from '../types';
import { useFormContext, useWatch } from 'react-hook-form';

interface ColumnsProps {
  readOnly: boolean;
  onDeleteCurriculumActivity: (id: string | number) => void;
  studyPlanTitleName?: string | null;
  studyPlanDegreeName?: string | null;
}

export function useColumns({
  readOnly,
  onDeleteCurriculumActivity,
  studyPlanTitleName,
  studyPlanDegreeName,
}: ColumnsProps) {
  const dictPrefix = `${prefix}.tableActivities`;
  const { t } = useTranslation();
  const texts = useMemo(
    () => ({
      headers: t<string, Record<string, string>>(`${dictPrefix}.headers`, {
        returnObjects: true,
      }),
    }),
    [t, dictPrefix],
  );

  const handleDeleteCurriculumActivity = useCallback(
    (id: string | number) => {
      onDeleteCurriculumActivity(id);
    },
    [onDeleteCurriculumActivity],
  );

  const { control, setValue } = useFormContext();

  const autoCalculate = useWatch({
    control,
    name: 'autoCalculate',
    defaultValue: false,
  });

  const curriculumActivities = useWatch({
    control,
    name: 'curriculumActivities',
  });

  const columns = useMemo((): ColumnTable<TableCurriculumDegreeActivity>[] => {
    const columnData: ColumnTable<TableCurriculumDegreeActivity>[] = [
      {
        columnName: 'name',
        width: '40px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.activities,
        cellFormat: ({ row }) => row.name,
      },
      {
        columnName: 'code',
        width: '15%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.code,
        cellFormat: ({ row }) => row.code,
      },
      {
        columnName: 'description',
        width: '25%',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.description,
        cellFormat: ({ row }) => row.description,
      },
      {
        columnName: 'allowGrades',
        width: '40px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.allowGrades,
        cellFormat: ({ row }) => {
          if (!row.allowGrades) {
            return t(`${dictPrefix}.allowGrades.hasMentionsNo`);
          }
          return t(`${dictPrefix}.allowGrades.hasMentionsYes`);
        },
      },
      {
        columnName: 'belongsTo',
        width: '60px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.belongsTo,
        cellFormat: ({ row }) => {
          if (!row.belongsTo) {
            return t(`${dictPrefix}.belongsTo.empty`);
          }
          if (row.belongsTo === 'both') {
            const namedParts = [studyPlanTitleName, studyPlanDegreeName]
              .filter((part): part is string => Boolean(part))
              .join(' · ');
            const tooltip = namedParts
              ? namedParts
              : (t(`${dictPrefix}.belongsTo.tooltipBoth`) as string);
            return (
              <span title={tooltip}>{t(`${dictPrefix}.belongsTo.both`)}</span>
            );
          }
          return t(`${dictPrefix}.belongsTo.${row.belongsTo}`);
        },
      },
      {
        columnName: 'percentage',
        width: '40px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.percentage,
        cellFormat: ({ row }) => {
          if (!autoCalculate) {
            return 'N/A';
          }

          if (!row.allowGrades) {
            return 'N/A';
          }

          const index = curriculumActivities?.findIndex(
            (activity: any) => activity.code === row.code,
          );

          if (index === -1 || index === undefined) {
            return 'Error';
          }

          return (
            <div className="d-flex justify-content-center">
              <div style={{ width: '70px' }}>
                <TextOutlinedInput
                  name={`curriculumActivities.${index}.percentage`}
                  value={row.percentage || ''}
                  label=""
                  readOnly={readOnly}
                  formatter={(value: string) => {
                    const numValue = value.toString()?.replace(/\D/g, '');
                    return Number(numValue) > 100 ? '100' : numValue;
                  }}
                  onChange={(e) => {
                    const updatedActivities = [...curriculumActivities];
                    updatedActivities[index] = {
                      ...updatedActivities[index],
                      percentage: e.target.value,
                    };
                    setValue('curriculumActivities', updatedActivities);
                  }}
                />
              </div>
            </div>
          );
        },
      },
      {
        columnName: 'id',
        width: '40px',
        thClassName: 'text-center',
        tdClassName: 'text-center',
        headerText: texts.headers.action,
        cellFormat: ({ row }) => (
          <span
            onClick={() => handleDeleteCurriculumActivity(row.code)}
            className="mr-2"
          >
            <Icon
              className={`cursor-pointer`}
              name="trash"
              key="eye"
              size="18px"
            />
          </span>
        ),
      },
    ];
    if (readOnly) {
      columnData.splice(-1);
    }
    return columnData;
  }, [
    t,
    texts,
    dictPrefix,
    readOnly,
    handleDeleteCurriculumActivity,
    autoCalculate,
    curriculumActivities,
    setValue,
    studyPlanTitleName,
    studyPlanDegreeName,
  ]);

  return columns;
}
